import { ApprovalProcess, ApprovalScopeEnum } from "@finway-group/shared/lib/models"
import { FormInstance } from "antd"
import { useWatch } from "antd/es/form/Form"
import { useCallback, useEffect, useMemo, useState } from "react"
import { DEBOUNCE_DURATION_MEDIUM, NO_TAX_RATE } from "Shared/config/consts"
import { usePreviousTyped } from "Shared/hooks/common.hooks"
import { useCompany } from "Shared/hooks/company.hooks"
import { useWorkflowById } from "Shared/hooks/workflow.hooks"
import { ApprovalProcessService } from "Shared/services"
import { amountDependentFields, getNextApproverId, getValuesChange, otherDependentFields, shouldBuildPurchaseOrderApprovalProcess } from "Shared/utils/approvalProcess.utils"
import { useDebouncedCallback } from "use-debounce"
import { useBuildApprovalMutation } from "../queries/purchaseOrder.queries"
import { PurchaseOrder, PurchaseOrderFormValues } from "../types/PurchaseOrder.interfaces"

export const approvalProcessDependentFields = new Set([...amountDependentFields, ...otherDependentFields, "quantity", "unitPrice"])

export const usePurchaseOrderApprovals = (form: FormInstance<PurchaseOrderFormValues>, purchaseOrder?: PurchaseOrder) => {
    const { mutateAsync: buildApprovalProcess, data: approvalProcessesData, isLoading: isBuildingApprovalProcess } = useBuildApprovalMutation()
    const [approvalProcesses, setApprovalProcesses] = useState<Array<ApprovalProcess>>(approvalProcessesData ?? [])

    // Memoize the workflow ID extraction so it updates when approvalProcesses changes
    const workflowId = useMemo(() => approvalProcesses?.find((ap) => ap.scope === ApprovalScopeEnum.PURCHASE_ORDER)?.workflow ?? null, [approvalProcesses])

    const workflow = useWorkflowById(workflowId)
    const company = useCompany()
    const formValues = useWatch<PurchaseOrderFormValues>([], form)
    const previousFormValues = usePreviousTyped<PurchaseOrderFormValues>(formValues)

    useEffect(() => {
        if (approvalProcesses && approvalProcesses.length > 0) {
            // Get the purchase order approval processp
            const poApprovalProcess = approvalProcesses.find((ap) => ap.scope === ApprovalScopeEnum.PURCHASE_ORDER)

            if (poApprovalProcess) {
                // Use getNextApproverId like expenses do
                const nextApproverId = getNextApproverId(approvalProcesses)

                if (nextApproverId) {
                    const currentApproverId = form.getFieldValue("nextApprover")
                    // Update if different from current value or if no current value is set
                    if (currentApproverId !== nextApproverId) {
                        form.setFieldValue("nextApprover", nextApproverId)
                    }
                }
            }
        }
    }, [approvalProcesses, form])

    const buildPurchaseOrderApprovalProcessInternal = async () => {
        const purchaseOrderData = form.getFieldsValue()

        if (purchaseOrder && !shouldBuildPurchaseOrderApprovalProcess(purchaseOrder, company, getValuesChange(previousFormValues, purchaseOrderData))) {
            return
        }

        const approvalProcess = await buildApprovalProcess({
            approvalProcessScopes: [ApprovalScopeEnum.PURCHASE_ORDER],
            purchaseOrderData: {
                lineItems: purchaseOrderData.lineItems.map((lineItem) => ({
                    ...lineItem,
                    taxRate: lineItem.taxRate?._id === NO_TAX_RATE ? undefined : lineItem.taxRate?._id,
                })),
                totalNetPrice: purchaseOrderData.totalNetPrice,
                currency: purchaseOrderData.currency,
                createCard: purchaseOrderData.createCard,
                vendor: { _id: purchaseOrderData.vendor?._id },
                requestedBy: { _id: purchaseOrderData.requestedBy?._id },
            },
        })

        setApprovalProcesses(approvalProcess)
    }

    const debouncedBuildPurchaseOrderApprovalProcess = useDebouncedCallback(buildPurchaseOrderApprovalProcessInternal, DEBOUNCE_DURATION_MEDIUM)

    const buildPurchaseOrderApprovalProcess = useCallback(() => {
        void debouncedBuildPurchaseOrderApprovalProcess.callback()
    }, [debouncedBuildPurchaseOrderApprovalProcess])

    const buildPurchaseOrderApprovalProcessWithApprover = async (approverId: string) => {
        const newApprovalProcess = ApprovalProcessService.getNewApprovalProcess(approverId, ApprovalScopeEnum.PURCHASE_ORDER, approvalProcesses ?? [])

        const mergedApprovalProcesses = await ApprovalProcessService.getMergedApprovalProcesses(approvalProcesses ?? [], newApprovalProcess, {
            prioritizeApproved: false,
        })

        setApprovalProcesses(mergedApprovalProcesses ?? [])
    }

    const confirmApprovalProcessChange = async (awaitConfirmation: boolean) => {
        const poApprovalProcesses = purchaseOrder?._id
            ? await ApprovalProcessService.getMergedApprovalProcesses(purchaseOrder?.approvalProcesses!, approvalProcesses!, {
                  awaitConfirmation,
                  prioritizeApproved: false,
              })
            : approvalProcesses

        return poApprovalProcesses
    }

    return {
        buildPurchaseOrderApprovalProcess,
        buildPurchaseOrderApprovalProcessWithApprover,
        setApprovalProcesses,
        approvalProcesses,
        workflow,
        confirmApprovalProcessChange,
        isBuildingApprovalProcess,
    }
}
